import { Element as SvgElement } from '@svgdotjs/svg.js';
import { Utils } from 'app/core/utils';
import { Point, Region } from 'core/geometry';

export interface DragInfo
{
  elementSnapshot: Region;
  firstPosition: Point;
  delta: Point;
}

export interface ClickInfo
{
  clientX: number;
  clientY: number;
}

export class DragSensor
{
  private readonly DRAG_THRESHOLD: number = 5;

  private dragInfo: DragInfo = undefined;
  private dragCursorStyles: Array<HTMLStyleElement> = [];
  private windowEventListeners: Object = {};
  private elementEventListeners: Object = {};

  public onDragBegin = (dragInfo: DragInfo) => {}; // eslint-disable-line
  public onDrag = (dragInfo: DragInfo) => {}; // eslint-disable-line
  public onDragEnd = () => {}; // eslint-disable-line
  public onClick = (click: ClickInfo) => {}; // eslint-disable-line

  constructor(private element: SvgElement | HTMLElement)
  {
    const withoutDefault = (handler) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      return handler(event);
    };

    const onlyIfDragging =
      (handler) => (event) =>
          this.dragInfo !== undefined
              ? handler(event)
              : undefined;

    const touchHandler =
      (handler) => (event) =>
          handler.bind(this)(
              event.touches[0].clientX, event.touches[0].clientY);

    const mouseHandler =
        (handler) => (event) =>
            handler.bind(this)(event.clientX, event.clientY);

    const simpleHandler = (handler) => (event) => handler.bind(this)();

    this.elementEventListeners['mousedown'] =
        withoutDefault(mouseHandler(this.beginDrag));
    this.elementEventListeners['touchstart'] =
        withoutDefault(touchHandler(this.beginDrag));

    const endDragHandler =
        onlyIfDragging(withoutDefault(simpleHandler(this.endDrag)));

    this.windowEventListeners['mouseup'] = endDragHandler;
    this.windowEventListeners['touchend'] = endDragHandler;
    this.windowEventListeners['touchcancel'] = endDragHandler;
    this.windowEventListeners['mousemove'] =
        onlyIfDragging(withoutDefault(mouseHandler(this.drag)));
    this.windowEventListeners['touchmove'] =
        onlyIfDragging(withoutDefault(touchHandler(this.drag)));

    for (const listener in this.elementEventListeners) {
      if (this.elementEventListeners.hasOwnProperty(listener)) {
        this.node.addEventListener(
            listener, this.elementEventListeners[listener]);
      }
    }
    for (const listener in this.windowEventListeners) {
      if (this.windowEventListeners.hasOwnProperty(listener)) {
        window.addEventListener(listener, this.windowEventListeners[listener]);
      }
    }
  }

  public destructor(): void
  {
    for (const listener in this.elementEventListeners) {
      if (this.elementEventListeners.hasOwnProperty(listener)) {
        this.node.removeEventListener(
            listener, this.elementEventListeners[listener]);
      }
    }
    for (const listener in this.windowEventListeners) {
      if (this.windowEventListeners.hasOwnProperty(listener)) {
        window.removeEventListener(
            listener, this.windowEventListeners[listener]);
      }
    }
  }

  private get node(): Element
  {
    return this.element instanceof HTMLElement
      ? this.element
      : this.element.node;
  }

  private beginDrag(x: number, y: number): void
  {
    Utils.ChangeDetection.pause();

    this.dragInfo = <DragInfo> {
      elementSnapshot: Region.fromSimilar(this.element instanceof HTMLElement
        ? this.element.getBoundingClientRect()
        : this.element.bbox()),
      firstPosition: new Point(x, y),
      delta: new Point(0, 0)
    };

    this.applyDragCursor();
    this.onDragBegin(this.dragInfo);
  }

  private applyDragCursor(): void
  {
    const cursor = getComputedStyle(this.node).cursor;
    if (cursor === 'auto' || cursor === 'default') {
      return;
    }

    const root = this.node.getRootNode();
    const containers = root instanceof ShadowRoot
      ? [document.head, root]
      : [document.head];

    this.dragCursorStyles = containers.map(container => {
      const style = document.createElement('style');
      style.textContent = `* { cursor: ${
        cursor === 'grab' ? 'grabbing' : cursor} !important; }`;
      return container.appendChild(style);
    });
  }

  private endDrag(): void {
    const pointerHasMoved =
      Math.abs(this.dragInfo.delta.x) > this.DRAG_THRESHOLD ||
      Math.abs(this.dragInfo.delta.y) > this.DRAG_THRESHOLD;
    const click: ClickInfo = {
      clientX: this.dragInfo.firstPosition.x,
      clientY: this.dragInfo.firstPosition.y,
    };

    this.dragInfo = undefined;
    this.dragCursorStyles.forEach(style => style.remove());
    this.dragCursorStyles = [];
    this.onDragEnd();

    if (! pointerHasMoved) {
      this.onClick(click);
    }
    else {
      const swallowClick = (event: Event) => event.stopPropagation();
      window.addEventListener('click', swallowClick, true);
      setTimeout(
          () => window.removeEventListener('click', swallowClick, true));
    }

    Utils.ChangeDetection.resume();
  }

  private drag(x: number, y: number): void {
    this.dragInfo.delta.x = x - this.dragInfo.firstPosition.x;
    this.dragInfo.delta.y = y - this.dragInfo.firstPosition.y;

    this.onDrag(this.dragInfo);
  }
}
